import type { AutoPublishSkipReason, SourceMessageStatus } from '@upme/contracts';
import { bcp47, resolveLocale } from '@upme/i18n';
import { importQueueRu } from '../../../i18n/ru/import-queue';

export const IMPORT_STATUS_VARIANT: Record<
  SourceMessageStatus,
  'default' | 'secondary' | 'outline' | 'destructive'
> = {
  PENDING: 'secondary',
  EXTRACTED: 'default',
  IGNORED: 'outline',
  // Badge has no `success` variant — outline + custom color in table component.
  IMPORTED: 'outline',
  REJECTED: 'destructive',
  DUPLICATE: 'outline',
};

// Cache formatters per locale+timezone to avoid re-creating Intl objects on every cell.
const dateTimeFmtCache = new Map<string, Intl.DateTimeFormat>();

function getDateTimeFmt(timezone: string, locale?: string): Intl.DateTimeFormat {
  const bcpLocale = bcp47(resolveLocale(locale));
  const cacheKey = `${bcpLocale}|${timezone}`;
  let fmt = dateTimeFmtCache.get(cacheKey);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat(bcpLocale, {
      timeZone: timezone,
      dateStyle: 'short',
      timeStyle: 'short',
    });
    dateTimeFmtCache.set(cacheKey, fmt);
  }
  return fmt;
}

/** `locale` — сырой `i18n.language` вызывающего компонента; без него (юнит-тесты) фолбэк на 'ru'. */
export function formatPostedAt(iso: string, timezone: string, locale?: string): string {
  return getDateTimeFmt(timezone, locale).format(new Date(iso));
}

export function formatConfidence(confidence: number | null | undefined): {
  label: string;
  tone: 'high' | 'mid' | 'low' | 'none';
} {
  if (confidence == null) return { label: '—', tone: 'none' };
  const pct = Math.round(confidence * 100);
  if (confidence >= 0.8) return { label: `${pct}%`, tone: 'high' };
  if (confidence >= 0.5) return { label: `${pct}%`, tone: 'mid' };
  return { label: `${pct}%`, tone: 'low' };
}

export function formatSimilarity(similarity: number | null | undefined): string {
  if (similarity == null) return '';
  return ` (${Math.round(similarity * 100)}%)`;
}

export type SkipReasonBadgeVariant = 'secondary' | 'warning' | 'destructive';

export interface SkipReasonBadge {
  label: string;
  variant: SkipReasonBadgeVariant;
}

export interface SkipReasonDetail {
  title: string;
  description: string;
  hint: string;
}

/**
 * Минимальный TFunction-совместимый интерфейс для переиспользования сообщений
 * import-queue-namespace в чистых lib-функциях: вызывающий компонент прокидывает
 * `t` из `useTranslation('import-queue')` (реальная локаль пользователя). Без
 * явного `t` (существующие юнит-тесты чистых функций) — фолбэк на RU-бандл
 * напрямую, чтобы не тянуть сюда React/react-i18next.
 */
export type ImportQueueT = (key: string, vars?: Record<string, string | number>) => string;

function getByPath(obj: unknown, path: string): unknown {
  return path
    .split('.')
    .reduce<unknown>(
      (acc, part) => (acc && typeof acc === 'object' ? (acc as Record<string, unknown>)[part] : undefined),
      obj,
    );
}

function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (_match, key: string) => String(vars[key] ?? ''));
}

const defaultT: ImportQueueT = (key, vars) => {
  const raw = getByPath(importQueueRu, key);
  return typeof raw === 'string' ? interpolate(raw, vars) : key;
};

/**
 * Короткий бейдж для строки таблицы. null означает «не показывать»
 * (NOT_AN_EVENT — у EXTRACTED такого не бывает; AUTOPUBLISH_DISABLED —
 * глобальное состояние, шум в каждой строке).
 */
export function formatAutoPublishSkipReasonBadge(
  reason: AutoPublishSkipReason,
  t: ImportQueueT = defaultT,
): SkipReasonBadge | null {
  switch (reason.code) {
    case 'LOW_CONFIDENCE':
      return {
        label: t('skipReason.lowConfidenceBadge', { pct: Math.round(reason.confidence * 100) }),
        variant: 'secondary',
      };
    case 'MULTI_DATE':
      return { label: t('skipReason.multiDateBadge', { count: reason.dates }), variant: 'warning' };
    case 'NO_DATE':
      return { label: t('skipReason.noDateBadge'), variant: 'warning' };
    case 'MISSING_TITLE':
      return { label: t('skipReason.missingTitleBadge'), variant: 'warning' };
    case 'NOT_AN_EVENT':
    case 'AUTOPUBLISH_DISABLED':
      return null;
  }
}

/**
 * Развёрнутая карточка для страницы детали. null — не показывать блок.
 */
export function formatAutoPublishSkipReasonDetail(
  reason: AutoPublishSkipReason,
  t: ImportQueueT = defaultT,
): SkipReasonDetail | null {
  switch (reason.code) {
    case 'LOW_CONFIDENCE':
      return {
        title: t('skipReason.lowConfidenceTitle'),
        description: t('skipReason.lowConfidenceDescription', {
          pct: Math.round(reason.confidence * 100),
          threshold: Math.round(reason.threshold * 100),
        }),
        hint: t('skipReason.lowConfidenceHint'),
      };
    case 'MULTI_DATE':
      return {
        title: t('skipReason.multiDateTitle'),
        description: t('skipReason.multiDateDescription', { count: reason.dates }),
        hint: t('skipReason.multiDateHint'),
      };
    case 'NO_DATE':
      return {
        title: t('skipReason.noDateTitle'),
        description: t('skipReason.noDateDescription'),
        hint: t('skipReason.noDateHint'),
      };
    case 'MISSING_TITLE':
      return {
        title: t('skipReason.missingTitleTitle'),
        description: t('skipReason.missingTitleDescription'),
        hint: t('skipReason.missingTitleHint'),
      };
    case 'NOT_AN_EVENT':
    case 'AUTOPUBLISH_DISABLED':
      return null;
  }
}
